import React, { useEffect, useState, useCallback, useMemo } from "react";
import style from "../psilideo-plague/psilideo-plague.module.css";
import PsilideoImage from "../../../../images/psilide-colored.png";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ParkOutlinedIcon from "@mui/icons-material/ParkOutlined";
import logger from "../../../../utils/logger";
import BracellRowExtras from "./BracellRowExtras";
import BracellLevelPanel from "./BracellLevelPanel";
import { diagnoseBracellPercevejo } from "../../config/bracellRegistryProfile";

function createRow(index, plantCount) {
  const pc = Math.max(1, plantCount);
  return {
    planta: (index % pc) + 1,
    parcela: Math.floor(index / pc) + 1,
    numeroInsetos: 0,
    numeroMassaOvos: 0,
    numeroFolhas: 1,
    desfolha_incidencia_percent: "0",
    dano_severidade: "Sem Dano",
  };
}

function groupByParcela(samples, plantCount) {
  const pc = Math.max(1, plantCount);
  const groups = [];
  for (let i = 0; i < samples.length; i += pc) {
    groups.push(samples.slice(i, i + pc));
  }
  return groups;
}

function BracellPsilideoPercevejo({ plague, infestation, data, plantCount, parcelas: parcelasProp }) {
  const [samples, setSamples] = useState([]);
  const isValidPlague =
    plague && typeof plague === "object" && plague.name && plague.scientific;

  const numParcelas = Math.max(1, Number(parcelasProp) || 2);
  const numPlantCount = Math.max(1, Number(plantCount) || 5);

  useEffect(() => {
    const total = numParcelas * numPlantCount;
    setSamples(Array.from({ length: total }, (_, i) => createRow(i, numPlantCount)));
  }, [numParcelas, numPlantCount]);

  const patchRow = useCallback((index, patch) => {
    setSamples((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  }, []);

  const bumpField = (field, index, delta) => {
    setSamples((prev) => {
      const next = [...prev];
      const cur = parseInt(next[index][field], 10) || 0;
      next[index] = {
        ...next[index],
        [field]: Math.max(0, cur + delta),
      };
      return next;
    });
  };

  useEffect(() => {
    if (!data || !isValidPlague) return;
    data(samples);
  }, [samples, data, isValidPlague]);

  const { lines, displayText, levelKey, diagnostic, prob } = useMemo(() => {
    const groups = groupByParcela(samples, numPlantCount);
    const insPorFolha = groups.map((group) => {
      const sumI = group.reduce((acc, s) => acc + (parseInt(s.numeroInsetos, 10) || 0), 0);
      const folhas = group.length * 1;
      return folhas > 0 ? sumI / folhas : 0;
    });
    const massPorFolha = groups.map((group) => {
      const sumM = group.reduce((acc, s) => acc + (parseInt(s.numeroMassaOvos, 10) || 0), 0);
      const folhas = group.length * 1;
      return folhas > 0 ? sumM / folhas : 0;
    });
    const insMean = insPorFolha.length > 0
      ? insPorFolha.reduce((a, b) => a + b, 0) / insPorFolha.length
      : 0;
    const massMean = massPorFolha.length > 0
      ? massPorFolha.reduce((a, b) => a + b, 0) / massPorFolha.length
      : 0;

    const totalI = samples.reduce((acc, s) => acc + (parseInt(s.numeroInsetos, 10) || 0), 0);
    const totalM = samples.reduce((acc, s) => acc + (parseInt(s.numeroMassaOvos, 10) || 0), 0);

    const d = diagnoseBracellPercevejo(insMean, massMean, 1);

    const ls = [
      { label: "Total de Insetos", value: totalI },
      { label: "Total de massas de ovos", value: totalM },
    ];
    if (numParcelas > 1) {
      insPorFolha.forEach((v, p) => {
        ls.push({ label: `Parcela ${p + 1} — Insetos por folha`, value: v.toFixed(2) });
      });
    }
    ls.push({ label: "Insetos por folha", value: insMean.toFixed(2) });
    ls.push({ label: "Massas de ovos por folha", value: massMean.toFixed(2) });

    return {
      lines: ls,
      displayText: d.diagnostic,
      levelKey: d.levelKey,
      diagnostic: d.diagnostic,
      prob: d.prob,
    };
  }, [samples, numPlantCount, numParcelas]);

  useEffect(() => {
    if (!isValidPlague) return;
    infestation({ diagnostic, prob });
  }, [diagnostic, prob, infestation, isValidPlague]);

  if (!isValidPlague) {
    logger.warn("Objeto de praga inválido:", plague);
    return null;
  }

  return (
    <div>
      <div className={style.card}>
        <div className={style["sample-plague"]}>
          <img src={PsilideoImage} alt="" />
          <div className={style["name-sample-plague"]}>
            <div>{plague.name}</div>
            <div>{plague.scientific}</div>
          </div>
        </div>

        <div className={style.samples}>
          {samples.map((sample, index) => {
            const parcelaStart = index % numPlantCount === 0;
            const parcelaNum = Math.floor(index / numPlantCount) + 1;
            return (
              <React.Fragment key={index}>
                {numParcelas > 1 && parcelaStart && (
                  <div style={{ fontWeight: "600", fontSize: "0.85rem", padding: "10px 0 4px", color: "#40a8c4" }}>
                    Parcela {parcelaNum}
                  </div>
                )}
                <div className={style["collected-sample-title"]}>
                  <div className={style["collected-sample-plant"]}>
                    <div className={style["collected-sample-plant-number"]}>
                      {sample.planta}° <ParkOutlinedIcon style={{ width: "0.8rem" }} />
                    </div>
                  </div>
                  <div className={style["psilideo-informations"]}>
                    <div className={style["information-unsheet"]}>
                      <div className={style.name}>N° de insetos</div>
                      <div className={style["container-inputs"]}>
                        <RemoveIcon
                          onClick={() => bumpField("numeroInsetos", index, -1)}
                          className={style.icon}
                        />
                        <input
                          value={sample.numeroInsetos}
                          className={style.input}
                          type="number"
                          min={0}
                          onChange={(e) =>
                            patchRow(index, {
                              numeroInsetos: Math.max(
                                0,
                                parseInt(e.target.value, 10) || 0
                              ),
                            })
                          }
                        />
                        <AddIcon
                          onClick={() => bumpField("numeroInsetos", index, 1)}
                          className={style.icon}
                        />
                      </div>
                    </div>
                    <div className={style["information-unsheet"]}>
                      <div className={style.name}>N° de massa de ovos</div>
                      <div className={style["container-inputs"]}>
                        <RemoveIcon
                          onClick={() => bumpField("numeroMassaOvos", index, -1)}
                          className={style.icon}
                        />
                        <input
                          value={sample.numeroMassaOvos}
                          className={style.input}
                          type="number"
                          min={0}
                          onChange={(e) =>
                            patchRow(index, {
                              numeroMassaOvos: Math.max(
                                0,
                                parseInt(e.target.value, 10) || 0
                              ),
                            })
                          }
                        />
                        <AddIcon
                          onClick={() => bumpField("numeroMassaOvos", index, 1)}
                          className={style.icon}
                        />
                      </div>
                    </div>
                  </div>
                  <BracellRowExtras
                    desfolha_incidencia_percent={sample.desfolha_incidencia_percent}
                    dano_severidade={sample.dano_severidade}
                    onPatch={(p) => patchRow(index, p)}
                  />
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <BracellLevelPanel
        lines={lines}
        displayText={displayText}
        levelKey={levelKey}
      />
    </div>
  );
}

export default BracellPsilideoPercevejo;
